import { PaperPlaneRight } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DOCUMENT_CHAT_MAX_CHARS } from "~/api/commentTypes";
import {
  useAvailableThinkers,
  useDocumentChat,
  useSendDocumentChat,
  useSuggestedThinkers,
} from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";
import { formatDateTime } from "~/foundation/utils/time";

/** Detail levels, kms's three. They change how much of each row is shown, nothing else. */
const MODES = ["simple", "medium", "expert"] as const;
type ChatMode = (typeof MODES)[number];

const MODE_KEY = "docchat.mode";
const thinkerKey = (documentId: string) => `docchat.thinker.${documentId}`;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A private window is allowed to forget a preference.
  }
}

/** kms's number: the "composing…" indicator clears itself after 30 s no matter what. */
const TYPING_TIMEOUT_MS = 30_000;

/**
 * The per-document chat (W8 §B.8 / S9).
 *
 * A side channel among the people looking at a document, with `@ai` as one more participant.
 * It is explicitly NOT the content-sync channel and NOT a conversation: there is no parent
 * row, no thinker assignment, no context pack — **the document is the scope**, one flat list.
 *
 * There is no socket here yet (§F.4 decision 1). `CollaborationHub` would push
 * `DocumentChatMessage` and a `DocumentChatTyping` frame that ONLY the AI responder ever
 * sends; a 4-second poll covers the first, and the second is reproduced locally: after you
 * mention `@ai`, this says the assistant is answering until its reply lands or 30 seconds
 * pass. That is the same promise the hub made, kept without the hub — and it is a promise
 * about our own request, so it cannot claim someone else is typing when they are not.
 */
export function DocumentChatPanel({
  documentId,
  canComment,
}: {
  documentId: string;
  canComment: boolean;
}) {
  const { t, i18n } = useTranslation();
  const chat = useDocumentChat(documentId, { poll: true });
  const send = useSendDocumentChat();
  const { data: thinkers } = useAvailableThinkers();
  const { data: suggested } = useSuggestedThinkers(documentId);

  const [mode, setMode] = useState<ChatMode>(
    () => (MODES.find((entry) => entry === readStored(MODE_KEY)) ?? "medium") as ChatMode,
  );
  const [pinned, setPinned] = useState<string>(() => readStored(thinkerKey(documentId)) ?? "");
  const [draft, setDraft] = useState("");
  const [awaitingAi, setAwaitingAi] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const messages = useMemo(() => chat.data ?? [], [chat.data]);
  const lastAiId = messages.filter((message) => message.authorType === "ai").at(-1)?.id ?? null;

  // The AI's answer arriving is what stops the indicator; the timeout is only the backstop.
  useEffect(() => {
    if (!awaitingAi) {
      return undefined;
    }
    const timer = window.setTimeout(() => setAwaitingAi(false), TYPING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [awaitingAi]);

  useEffect(() => {
    if (lastAiId) {
      setAwaitingAi(false);
    }
  }, [lastAiId]);

  // A chat is read from the bottom. Unlike the transcript there is no streaming here, so a new
  // row is always something a person just said — following it is what everybody expects.
  const messageCount = messages.length;
  useEffect(() => {
    const element = scrollRef.current;
    // `messageCount` is the trigger AND the guard: an empty room has nothing to scroll to.
    if (element && messageCount > 0) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messageCount]);

  const submit = () => {
    const text = draft.trim();
    if (!text || send.isPending) {
      return;
    }
    setDraft("");
    setNotice(null);
    if (/@ai\b/i.test(text)) {
      setAwaitingAi(true);
    }
    send.mutate(
      { documentId, text },
      {
        onError: () => {
          setAwaitingAi(false);
          setNotice(t("Couldn't send — reconnecting, please retry"));
          // The draft is given back rather than lost: a message that failed to send is still
          // something the person wrote.
          setDraft(text);
        },
      },
    );
  };

  const overLimit = draft.trim().length > DOCUMENT_CHAT_MAX_CHARS;
  const pinnedName = thinkers?.find((entry) => entry.id === pinned)?.name ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <div className="flex gap-0.5" role="group" aria-label={t("Chat detail level")}>
          {MODES.map((entry) => (
            <button
              key={entry}
              type="button"
              aria-pressed={mode === entry}
              onClick={() => {
                setMode(entry);
                writeStored(MODE_KEY, entry);
              }}
              className={cn(
                "rounded-sm px-2 py-0.5 text-caption",
                mode === entry
                  ? "bg-accent text-accent-text"
                  : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
              )}
            >
              {entry === "simple" ? t("Simple") : entry === "medium" ? t("Medium") : t("Expert")}
            </button>
          ))}
        </div>

        {mode !== "simple" ? (
          <label className="ms-auto flex items-center gap-1 text-caption text-text-tertiary">
            {t("Assistant")}
            {/* No `Select` in the design system yet — this is the hand-rolled shape §E.2 names. */}
            <select
              value={pinned}
              onChange={(event) => {
                setPinned(event.target.value);
                writeStored(thinkerKey(documentId), event.target.value);
              }}
              className="h-7 rounded-sm border border-input-border bg-bg px-2 text-caption text-text"
            >
              <option value="">{t("Auto")}</option>
              {(thinkers ?? []).map((thinker) => (
                <option key={thinker.id} value={thinker.id}>
                  {thinker.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {mode !== "simple" && pinned ? (
        <Text variant="small" className="text-text-tertiary">
          {t("Pinned for you on this document: {{name}}", { name: pinnedName ?? pinned })}
        </Text>
      ) : null}

      <div
        ref={scrollRef}
        className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
      >
        {chat.isLoading ? <Spinner label={t("Loading")} /> : null}
        {!chat.isLoading && messages.length === 0 ? (
          <Empty message={t("No messages yet. Start the conversation.")} />
        ) : null}

        {messages.map((message) => (
          <article key={message.id} className="flex flex-col gap-0.5">
            <header className="flex items-center gap-2">
              <Text variant="small" className="font-medium text-text">
                {message.authorName}
              </Text>
              {message.authorType === "ai" ? (
                <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 text-caption text-accent">
                  {t("AI")}
                </span>
              ) : null}
              {mode === "expert" && message.createdAt ? (
                <span className="text-caption text-text-tertiary">
                  {formatDateTime(message.createdAt, i18n.language)}
                </span>
              ) : null}
            </header>
            <p className="whitespace-pre-wrap text-start text-body text-text-secondary">
              {message.content}
            </p>
          </article>
        ))}

        {awaitingAi ? (
          <Text variant="small" className="text-text-tertiary">
            {t("The assistant is composing…")}
          </Text>
        ) : null}
      </div>

      {mode === "expert" && (suggested ?? []).length > 0 ? (
        <div className="flex flex-wrap gap-1" role="group" aria-label={t("Suggested")}>
          {(suggested ?? []).slice(0, 5).map((thinker) => (
            <button
              key={thinker.id}
              type="button"
              onClick={() => setDraft((current) => `${current}@${thinker.name} `)}
              className="rounded-sm bg-bg-secondary px-2 py-0.5 text-caption text-text-secondary hover:bg-hover-bg"
            >
              @{thinker.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-1 border-t border-divider pt-2">
        <textarea
          value={draft}
          aria-label={t("Message")}
          placeholder={t("Message… (@ai to ask the assistant)")}
          disabled={!canComment}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className="min-h-9 w-full flex-1 resize-none rounded-sm border border-input-border bg-bg p-2 text-body text-text disabled:opacity-50"
        />
        <Button
          size="sm"
          variant="primary"
          icon={PaperPlaneRight}
          aria-label={t("Send")}
          disabled={!canComment || draft.trim().length === 0 || send.isPending}
          onClick={submit}
        >
          {t("Send")}
        </Button>
      </div>

      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {notice ??
          (overLimit
            ? // Saying so beats the server's silent truncation.
              t("Only the first {{max}} characters will be sent.", {
                max: DOCUMENT_CHAT_MAX_CHARS,
              })
            : !canComment
              ? t("You can read this chat, but not post to it.")
              : t("Chat with collaborators. Mention @ai for the assistant."))}
      </div>
    </div>
  );
}
